import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { LongPressDirective } from '@freelancer/directives';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import type {
  CalloutTriggerComponentInterface,
  CalloutTriggerOpenInterface,
} from './callout.types';

@Component({
  standalone: true,
  selector: 'fl-callout-trigger',
  template: ` <ng-content></ng-content> `,
  styleUrls: ['./callout-trigger.component.scss'],
  hostDirectives: [
    {
      directive: LongPressDirective,
      outputs: ['longPressedMobile: handleLongPressedMobile'],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalloutTriggerComponent
  implements CalloutTriggerComponentInterface
{
  containerRef = inject(ViewContainerRef);

  @Output() trigger = new EventEmitter<MouseEvent>();
  @Output() open = new EventEmitter<CalloutTriggerOpenInterface>();
  @Output() close = new EventEmitter<void>();

  @Input() disableMobile = false;

  @Input() disabled = false;

  hover: boolean;
  mobileLongPress: boolean;

  @HostBinding('attr.data-mobile-force-click') mobileForceClick: boolean;
  @HostBinding('attr.data-hover') hoverAttr: boolean;
  @HostBinding('attr.data-placement') placement: string;
  @HostBinding('attr.data-callout-open') calloutOpen = false;

  /**
   * Reason for exposing the nativeElement is to allow the parent component to
   * pass the nativeElement to the callout content component to prevent emitting
   * outsideClick event after clicking the trigger.
   */
  get nativeElement(): HTMLElement {
    return this.containerRef.element.nativeElement as HTMLElement;
  }

  @HostListener('click', ['$event'])
  handleTriggerClick(event: MouseEvent): void {
    /**
     * Force open if hover is set to true
     * to support touch devices as well.
     *
     * Note: Clicking the trigger again or outside
     * the callout will close the callout.
     */
    if (this.disabled) {
      return;
    }

    if (this.isMobileViewport() && this.disableMobile) {
      return;
    }

    if (!this.isMobileViewport() && this.hover) {
      this.open.emit({ isPinnedOnClick: true });

      return;
    }

    if (
      this.isMobileViewport() &&
      this.mobileLongPress &&
      !this.mobileForceClick
    ) {
      return;
    }

    this.trigger.emit(event);
  }

  @HostListener('handleLongPressedMobile', ['$event'])
  handleLongPressedMobile(): void {
    if (!this.mobileLongPress) {
      return;
    }

    this.open.emit();
  }

  @HostListener('mouseenter')
  handleTriggerMouseover(): void {
    // Prevent this from running on touch device as it suppress
    // click event from triggering
    if (!this.isMobileViewport() && !this.isTouchDevice()) {
      this.open.emit();
    }
  }

  @HostListener('mouseleave')
  handleTriggerMouseout(): void {
    if (!this.isMobileViewport() && !this.isTouchDevice()) {
      this.close.emit();
    }
  }

  getElementBoundingRect(): DOMRect {
    return this.nativeElement.getBoundingClientRect();
  }

  setOptions(options: {
    hover: boolean;
    mobileForceClick: boolean;
    mobileLongPress: boolean;
    placement: string;
  }): void {
    this.hover = options.hover;
    this.hoverAttr = options.hover;
    this.mobileForceClick = options.mobileForceClick;
    this.mobileLongPress = options.mobileLongPress;
    this.placement = options.placement;
  }

  private isMobileViewport(): boolean {
    return window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);
  }

  private isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
}
